import { Box } from '@material-ui/core';
import { PhotoCamera, UnfoldLess, UnfoldMore } from '@material-ui/icons';
import { useBaseStore, useUserStore } from '@roc/feature-app-core';
import { DocumentForms, useDocumentStore } from '@roc/feature-documents';
import { useLoanStore } from '@roc/feature-loans';
import { LoanProcess } from '@roc/feature-utils';
import { Button, PDFPreview, PrintableArea, PrintableAreaRef, Tooltip } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { StatusFilterField } from './components/common/statusFilterField';
import { VirtualScroll } from './components/common/virtualScroll';
import { mapStatusFilter, SHOW_ALL_TO_DOS, SHOW_IN_REVIEW_AND_PENDING_TO_DOS } from './utils/loanDetailsConstants';
import { filterTodosByStatus, useIframeMessaging } from './utils/todoUtils';
import { TaskTodoCardCompponent } from './components/cards/todoTaskCard';
import { PriorityFilterField } from './components/common/priorityFilterField';

export const UnderwritingInternal = observer(() => {
  const { globalStore } = useBaseStore();
  const { userStore } = useUserStore();
  const { loanStore } = useLoanStore();
  const { documentStore } = useDocumentStore();
  const { prioritiesFilter } = documentStore?.loanTodosStore;
  const [statusFilter, setStatusFilter] = useState(SHOW_IN_REVIEW_AND_PENDING_TO_DOS);
  const [filterBy, setFilterBy] = useState(mapStatusFilter[statusFilter]);
  const [prioritiesSelected, setPrioritiesSelected] = useState([]);
  const params = useParams<{ loanId: string }>();
  const loanId = parseInt(params.loanId);
  const currentForm = documentStore.documentFormStore.currentForm;
  const loanProcess = LoanProcess.UNDERWRITING;
  const printableAreaRef = useRef<PrintableAreaRef>(null);
  const [expandAll, setExpandAll] = useState(0);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [collapseAll, setCollapseAll] = useState(0);
  const isTaskFrameworkEnabled = !!loanStore?.loanDetails?.taskFrameworkEnabled;

  const todos =
    documentStore.loanTodosStore.todosByLoanProcess[LoanProcess.UNDERWRITING] ??
    [];
  const loadTodos = () => {
    documentStore.loanTodosStore.fetchTodos(loanId, LoanProcess.UNDERWRITING, null, null);
  };

  useEffect(() => {
    userStore.getUserInfoAndEntitlements();
    loanStore.fetchLoanDetails(loanId);
    loadTodos();
  }, [loanId]);

  useIframeMessaging({
    reloadTodos() {
      loadTodos();
    },
  });

  const download = () => {
    printableAreaRef.current.downloadAsPdf(`Loan ${loanId} Documents.pdf`, {
      singlePage: true,
    });
  };

  useEffect(() => {
    if (!currentForm && todos.length > 0) {
      loadTodos();
    }
  }, [currentForm]);

  useEffect(() => {
    loadTodos();
  }, [prioritiesFilter]);

  if (!globalStore.userFeatures) {
    return null;
  }

  const filteredTodos = filterTodosByStatus(todos, filterBy);

  const onStatusFilterChange = (val) => {
    if (val === SHOW_ALL_TO_DOS) {
      setCollapseAll(expandAll + 1);
    } else {
      setExpandAll(collapseAll + 1);
    }
    setStatusFilter(val);
    setFilterBy(mapStatusFilter[val]);
  }

  const onPrioritiesFilterChange = (options) => {
    setPrioritiesSelected(options);
    documentStore?.loanTodosStore.setPrioritiesFilter(options);
  }

  return (
    <VirtualScroll>
      <Box pb={2} display="flex" justifyContent="flex-end">
        <Box pr={2}>
          <Tooltip title="Expand all sections" placement='bottom'>
            <div>
              <Button testId="expandAll" onClick={() => {
                setExpandAll(collapseAll + 1);
              }} variant="outlined" style={{
                letterSpacing: 'normal',
                textTransform: 'none',
              }}>
                <Box height={'31px'} display="flex" alignItems="center">
                  <UnfoldMore />&nbsp;&nbsp;Expand All
                </Box>
              </Button>
            </div>
          </Tooltip>
        </Box>
        <Box pr={2}>
          <Tooltip title="Collapse all sections" placement='bottom'>
            <div>
              <Button testId="collapseAll" onClick={() => {
                setCollapseAll(expandAll + 1);
              }} variant="outlined" style={{
                letterSpacing: 'normal',
                textTransform: 'none',
              }}>
                <Box height={'31px'} display="flex" alignItems="center">
                  <UnfoldLess />&nbsp;&nbsp;Collapse All
                </Box>
              </Button>
            </div>
          </Tooltip>
        </Box>
        <Box pr={2}>
          <Tooltip title="Take screenshot" placement='bottom'>
            <div>
              <Button testId="downloadAll" onClick={download} variant="outlined" style={{
                letterSpacing: 'normal',
                textTransform: 'none',
              }}>
                <Box height={'31px'} display="flex" alignItems="center">
                  <PhotoCamera />&nbsp;&nbsp;Take Screenshot
                </Box>
              </Button>
            </div>
          </Tooltip>
        </Box>
        {isTaskFrameworkEnabled &&
          <Box pr={2}>
            <PriorityFilterField
              value={prioritiesSelected}
              onChange={onPrioritiesFilterChange}
            />
          </Box>
        }
        <StatusFilterField
          value={statusFilter}
          onChange={onStatusFilterChange}
          version="v2"
        />
      </Box>
      <PrintableArea ref={printableAreaRef}>
        <TaskTodoCardCompponent
          loanProcess={LoanProcess.UNDERWRITING}
          todos={filteredTodos}
          showSectionActions
          showDateColumn={true}
          showStatusColumn={true}
          showPriorityColumn={true}
          showAssigneeColumn={true}
          showFileName
          expandAll={expandAll}
          collapseAll={collapseAll}
          showDownloadTodo
          filteredBy={filterBy}
        />
      </PrintableArea>
      <DocumentForms loanId={loanId?.toString()} />
      <PDFPreview
        open={documentStore.documentPreview != null}
        title={documentStore.documentPreview?.title}
        data={documentStore.documentPreview?.data}
        dataV2={documentStore.documentPreview?.data}
        headers={documentStore.documentPreview?.headers}
        onClose={() => documentStore.closeDocumentPreview()}
      />
    </VirtualScroll>
  );
});
